import CallOutlinedIcon from "@mui/icons-material/CallOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Typography from "@mui/material/Typography"
import { Device } from "@twilio/voice-sdk"
import { useCallback, useEffect, useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../common/axios"
import Column from "../../common/components/Column"
import Row from "../../common/components/Row"
import { environmentIsDevelop } from "../../common/utils"
import { apiErrorMessage, clientAndCampaign } from "./podDialer"

const POLL_MS = 15000

function formatWhen(iso) {
  return iso
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : ""
}

/** Recording fetched with the agent's token and played from a blob URL. */
function RecordingPlayer({ voicemailId, hasRecording }) {
  const axios = useAuthorizedAxiosClient()
  const [src, setSrc] = useState(null)

  useEffect(() => {
    if (!hasRecording) return undefined
    let url = null
    let cancelled = false
    axios
      .get(`/pods/api/agent/voicemails/${voicemailId}/audio/`, {
        responseType: "blob",
      })
      .then(({ data }) => {
        if (cancelled) return
        url = URL.createObjectURL(data)
        setSrc(url)
      })
      .catch(() => setSrc(null))
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [axios, hasRecording, voicemailId])

  if (!hasRecording) {
    return (
      <Typography variant="body2" color="text.secondary">
        The recording is still processing.
      </Typography>
    )
  }
  return src ? (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio controls src={src} aria-label="Voicemail recording" />
  ) : null
}

/**
 * Voicemails left on the pod's numbers, transcribed. Call back uses the
 * softphone from the lead's pod number; agents disconnect from the dialer first.
 */
export default function PodVoicemailsPanel({ dialerConnected, onChanged }) {
  const axios = useAuthorizedAxiosClient()
  const [scope, setScope] = useState("mine")
  const [showHandled, setShowHandled] = useState(false)
  const [rows, setRows] = useState([])
  const [selected, setSelected] = useState(null)
  const [reply, setReply] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [isCalling, setIsCalling] = useState(false)
  const [notice, setNotice] = useState("")
  const [error, setError] = useState("")
  const deviceRef = useRef(null)
  const callRef = useRef(null)

  const load = useCallback(
    () =>
      axios
        .get("/pods/api/agent/voicemails/", {
          params: { scope, handled: showHandled ? 1 : 0 },
        })
        .then(({ data }) => setRows(data.results || []))
        .catch(() => setError("Could not load voicemails.")),
    [axios, scope, showHandled]
  )

  useEffect(() => {
    load()
    const timer = window.setInterval(load, POLL_MS)
    return () => window.clearInterval(timer)
  }, [load])

  useEffect(
    () => () => {
      callRef.current?.disconnect()
      deviceRef.current?.destroy()
    },
    []
  )

  function open(row) {
    setNotice("")
    setReply("")
    axios
      .get(`/pods/api/agent/voicemails/${row.id}/`)
      .then(({ data }) => {
        setSelected(data)
        load()
        onChanged?.()
      })
      .catch(() => setError("Could not open the voicemail."))
  }

  function markHandled(handled) {
    axios
      .post(`/pods/api/agent/voicemails/${selected.id}/handled/`, { handled })
      .then(({ data }) => {
        setSelected(data)
        load()
        onChanged?.()
      })
      .catch(() => setError("Could not update the voicemail."))
  }

  function textBack() {
    setIsSending(true)
    axios
      .post(`/pods/api/agent/voicemails/${selected.id}/text/`, {
        message: reply,
      })
      .then(() => {
        setReply("")
        setNotice("Text sent. Replies show up in Pod texts.")
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not send.")))
      .finally(() => setIsSending(false))
  }

  async function callBack() {
    setIsCalling(true)
    setError("")
    try {
      const { data } = await axios.get("/pods/api/agent/voice-token/")
      deviceRef.current?.destroy()
      const device = new Device(data.token, {
        logLevel: environmentIsDevelop() ? 1 : 5,
        codecPreferences: ["opus", "pcmu"],
      })
      deviceRef.current = device
      const call = await device.connect({
        params: { PodVoicemailId: String(selected.id), To: "callback" },
      })
      callRef.current = call
      setNotice("Calling…")
      call.on("disconnect", () => {
        callRef.current = null
        setNotice("Call ended.")
        load()
      })
    } catch (err) {
      setError(apiErrorMessage(err, "Could not start the call."))
    } finally {
      setIsCalling(false)
    }
  }

  return (
    <Column gap={2} sx={{ pt: 2 }}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      <Row sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope}
          onChange={(_, value) => value && setScope(value)}
        >
          <ToggleButton value="mine">Mine</ToggleButton>
          <ToggleButton value="all">Whole pod</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showHandled}
              onChange={(e) => setShowHandled(e.target.checked)}
            />
          }
          label="Show handled"
        />
      </Row>
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: 420,
        }}
      >
        <List
          dense
          sx={{
            width: { xs: "100%", md: 360 },
            borderRight: { md: "1px solid" },
            borderColor: "divider",
            overflowY: "auto",
          }}
        >
          {rows.map((row) => (
            <ListItemButton
              key={row.id}
              selected={selected?.id === row.id}
              onClick={() => open(row)}
            >
              <ListItemText
                primary={`${
                  row.lead_name || row.from_phone
                } · ${clientAndCampaign(row.client, row.campaign)}`}
                primaryTypographyProps={{
                  fontWeight: row.is_read || row.handled ? 500 : 700,
                  noWrap: true,
                }}
                secondary={`${formatWhen(row.received_at)}${
                  row.owner?.is_me ? "" : ` · ${row.owner?.name}`
                } — ${row.preview || "Transcribing…"}`}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
          ))}
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No voicemails.
            </Typography>
          ) : null}
        </List>
        <Column sx={{ flex: 1, p: 2, gap: 1.5 }}>
          {selected ? (
            <>
              <Column gap={0.25}>
                <Typography variant="h6" fontWeight={700}>
                  {selected.lead_name || selected.from_phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clientAndCampaign(selected.client, selected.campaign)} ·{" "}
                  {selected.from_phone} · {formatWhen(selected.received_at)}
                </Typography>
              </Column>
              <RecordingPlayer
                voicemailId={selected.id}
                hasRecording={selected.has_recording}
              />
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {selected.transcript ||
                  (selected.transcription_status === "pending"
                    ? "Transcribing…"
                    : "No transcript.")}
              </Typography>
              {notice ? <Alert severity="info">{notice}</Alert> : null}
              <Row sx={{ gap: 1, flexWrap: "wrap" }}>
                <LoadingButton
                  variant="contained"
                  startIcon={<CallOutlinedIcon />}
                  loading={isCalling}
                  disabled={dialerConnected}
                  title={
                    dialerConnected
                      ? "Disconnect from the dialer to call back."
                      : undefined
                  }
                  onClick={callBack}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Call back
                </LoadingButton>
                <Button
                  variant="outlined"
                  onClick={() => markHandled(!selected.handled)}
                  sx={{ textTransform: "none" }}
                >
                  {selected.handled ? "Mark not handled" : "Mark handled"}
                </Button>
              </Row>
              {dialerConnected ? (
                <Typography variant="caption" color="text.secondary">
                  Disconnect from the dialer to call back.
                </Typography>
              ) : null}
              <Row sx={{ gap: 1, alignItems: "flex-end" }}>
                <TextField
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Text back…"
                  multiline
                  minRows={2}
                  fullWidth
                  inputProps={{ "aria-label": "Text back" }}
                />
                <LoadingButton
                  variant="outlined"
                  loading={isSending}
                  disabled={!reply.trim()}
                  onClick={textBack}
                >
                  Send
                </LoadingButton>
              </Row>
            </>
          ) : (
            <Typography color="text.secondary">Pick a voicemail.</Typography>
          )}
        </Column>
      </Paper>
    </Column>
  )
}
